import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Events } from 'ionic-angular';
import { APP_CONFIG } from '../../environment';
import { Transaction, TransactionViewType } from '../../model/myria/app/transaction';
import { BridgePartnerCheckoutRequest, BridgePartnerCheckoutResult, BridgePartnerCheckoutStatus } from '../../model/types/bridge-types';
import { DingTopUpErrorCode } from '../../model/types/ding-top-up.const';
import { StorageType } from '../../model/types/storage-type';
import { ConfigProvider } from '../config/config';
import { HttpProvider } from '../http/http';
import { LoadingProvider } from '../loading/loading';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { EVENTS_NAME } from '../myria/app-variables';
import { CustomersProvider } from '../myria/customer-provider/customers-provider';
import { FeatureFlagTypes, UtilsProvider } from '../myria/utils-provider';
import { PartnerCheckoutProvider } from '../partner-checkout/partner-checkout';
import { RouteProvider } from '../route/route';
import { StorageProvider } from '../storage/storage';

export interface IDingConsentRecord {
  version: number;
  acceptedAt: string;
}

export interface IDingCheckoutFailure {
  errorCode: DingTopUpErrorCode;
  status?: BridgePartnerCheckoutStatus;
  reason?: string;
  retVal?: any;
}

interface IDingTokenResult {
  token: string;
  retVal?: any;
  stop?: boolean;
}

interface IDingFlow {
  ownerId: string;
  startedAt: number;
}

@Injectable()
export class DingTopUpProvider {
  static readonly PARTNER = 'ding';
  static readonly PRODUCT_VARIANT = 'OneApp';
  static readonly RETURN_URL = 'https://www.riamoneytransfer.com/topup/ding/return';
  // Increase this value when the consent text or the Ding terms change. Then each customer must accept again.
  static readonly CONSENT_VERSION = 2;
  static readonly MAX_EXPIRED_RETRIES = 1;

  private static readonly EXIT_STATUSES: BridgePartnerCheckoutStatus[] = ['success', 'cancelled', 'closed'];
  private static readonly LOAD_FAILURE_REASONS = [PartnerCheckoutProvider.LOAD_ERROR_REASON, PartnerCheckoutProvider.LOAD_TIMEOUT_REASON];
  private static readonly AUTH_FAILURE_STATUSES = [401, 403];
  private static readonly TOKEN_TIMEOUT_MARGIN_MS = 5000;

  private flow: IDingFlow = null;

  constructor(
    private httpProvider: HttpProvider,
    private storageProvider: StorageProvider,
    private routeProvider: RouteProvider,
    private partnerCheckoutProvider: PartnerCheckoutProvider,
    private utilsProvider: UtilsProvider,
    private customersProvider: CustomersProvider,
    private loadingProvider: LoadingProvider,
    private translate: TranslateService,
    events: Events
  ) {
    events.subscribe(EVENTS_NAME.LOGOUT, () => this.endFlow('logout'));
  }

  // A response action can stop the HTTP provider before it settles the request, so the flow uses its own time limit.
  static tokenTimeoutMs(): number {
    return ConfigProvider.settings.connectionTimeout * 1000 + DingTopUpProvider.TOKEN_TIMEOUT_MARGIN_MS;
  }

  static parseConsentRecord(raw: any): IDingConsentRecord {
    try {
      const record = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return record && typeof record === 'object' ? record : null;
    } catch (ex) {
      return null;
    }
  }

  static buildCheckoutUrl(locale: string): string {
    return `${APP_CONFIG.dingBaseUrl}?lang=${encodeURIComponent(locale || '')}&returnUrl=${encodeURIComponent(DingTopUpProvider.RETURN_URL)}`;
  }

  /**
   * Gives true when the rmt5_vue_mobile_top_up flag is on. The customer id has no effect on the result.
   * Thus a lite user also sees the entry points.
   */
  async isAvailable(): Promise<boolean> {
    return !!(await this.utilsProvider.getFeatureFlagData(FeatureFlagTypes.Rmt5VueBridgeMobileTopUp));
  }

  async isBannerVisible(): Promise<boolean> {
    const key = this.getBannerDismissedKey();
    if (!key || !(await this.isAvailable())) {
      return false;
    }
    return String(await this.storageProvider.get(key)) !== 'true';
  }

  async dismissBanner(): Promise<void> {
    const key = this.getBannerDismissedKey();
    if (key) {
      await this.storageProvider.set(key, 'true');
    }
  }

  /**
   * Starts the Ding top-up flow. The flow shows the consent sheet if necessary. Then it opens the Ding checkout.
   * The consent and the in-flight guard use the customer id. A lite user has no customer id, so they use the reserved customer id.
   */
  async start(): Promise<void> {
    const ownerId = this.getOwnerId();
    if (this.flow && this.flow.ownerId === ownerId) {
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Ding top-up: start ignored, a flow is in progress', { runningMs: Date.now() - this.flow.startedAt });
      return;
    }
    if (this.flow) {
      this.endFlow('customer change');
    }
    const flow: IDingFlow = { ownerId, startedAt: Date.now() };
    this.flow = flow;
    try {
      if (!(await this.isAvailable())) {
        LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Ding top-up: start ignored, the top-up is not available');
        return;
      }
      if (!ownerId) {
        LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Ding top-up: start ignored, no customer id and no reserved customer id');
        return;
      }
      if (!(await this.ensureConsent())) {
        return;
      }
      await this.checkoutWithRetry(flow);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Ding top-up: flow failed');
    } finally {
      if (this.flow === flow) {
        this.flow = null;
      }
    }
  }

  async hasConsent(): Promise<boolean> {
    const key = this.getConsentKey();
    if (!key) {
      return false;
    }
    const record = DingTopUpProvider.parseConsentRecord(await this.storageProvider.get(key));
    return !!record && record.version === DingTopUpProvider.CONSENT_VERSION;
  }

  async saveConsent(): Promise<void> {
    const key = this.getConsentKey();
    if (!key) {
      return;
    }
    const record: IDingConsentRecord = { version: DingTopUpProvider.CONSENT_VERSION, acceptedAt: new Date().toISOString() };
    await this.storageProvider.set(key, JSON.stringify(record));
  }

  // Ask before the token request: the token request already shares the customer data with Ding.
  private async ensureConsent(): Promise<boolean> {
    if (await this.hasConsent()) {
      return true;
    }
    const accepted = await this.openChoiceSheet({
      title: this.translate.instant('page.MobileTopUp.ConsentTitle'),
      subtitle: this.translate.instant('page.MobileTopUp.ConsentDescription'),
      primaryButtonText: this.translate.instant('page.MobileTopUp.ConsentContinue'),
      secondaryButtonText: this.translate.instant('page.MobileTopUp.ConsentNotNow'),
    });
    if (accepted === null) {
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Ding top-up: the consent sheet could not open');
    }
    if (!accepted) {
      return false;
    }
    try {
      await this.saveConsent();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Ding top-up: consent not saved');
    }
    return true;
  }

  private async checkoutWithRetry(flow: IDingFlow): Promise<void> {
    let failure = await this.runCheckout(flow);
    while (failure && await this.isFlowActive(flow)) {
      LoggerProvider.error(this, LoggerEvent.ProviderException, 'Ding top-up: checkout failed', failure);
      if (!(await this.askRetry(failure.errorCode))) {
        return;
      }
      failure = await this.runCheckout(flow);
    }
  }

  private async runCheckout(flow: IDingFlow): Promise<IDingCheckoutFailure> {
    try {
      for (let attempt = 0; attempt <= DingTopUpProvider.MAX_EXPIRED_RETRIES; attempt++) {
        if (!(await this.isFlowActive(flow))) {
          return null;
        }
        const minted = await this.mintToken();
        if (minted.stop) {
          return null;
        }
        if (!minted.token) {
          return { errorCode: DingTopUpErrorCode.TokenUnavailable, retVal: minted.retVal };
        }
        const result = await this.partnerCheckoutProvider.open(this.buildRequest(minted.token), DingTopUpProvider.PRODUCT_VARIANT);
        const status = result && result.status ? result.status : 'failed';
        if (status === 'expired' && attempt < DingTopUpProvider.MAX_EXPIRED_RETRIES) {
          continue;
        }
        return this.failureFor(status, result);
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Ding top-up: checkout failed');
    }
    return { errorCode: DingTopUpErrorCode.CheckoutFailed };
  }

  private failureFor(status: BridgePartnerCheckoutStatus, result: BridgePartnerCheckoutResult): IDingCheckoutFailure {
    if (DingTopUpProvider.EXIT_STATUSES.indexOf(status) >= 0) {
      return null;
    }
    const reason = result && result.params ? result.params[PartnerCheckoutProvider.RESULT_REASON_PARAM] : undefined;
    if (status === 'expired') {
      return { errorCode: DingTopUpErrorCode.TokenExpired, status, reason };
    }
    // Do not show the error sheet when the checkout did not load. RMT5 (useMobileTopUp) also stops the flow without an error, so the two UIs operate the same.
    if (reason && DingTopUpProvider.LOAD_FAILURE_REASONS.indexOf(reason) >= 0) {
      LoggerProvider.info(this, 'Ding top-up: checkout did not load', { reason });
      return null;
    }
    return { errorCode: DingTopUpErrorCode.CheckoutFailed, status, reason };
  }

  // Use the corridor of the customer, not the current order: on the home page the order gets its country from device storage, which an earlier customer can have set.
  private async resolveCountryTo(): Promise<string> {
    // A lite user has no customer id and no transactions. Without a customer id the transactions request goes to a wrong URL.
    if (!this.httpProvider.getAuthCustomerId()) {
      return undefined;
    }
    try {
      const transactions: Transaction[] = this.customersProvider.getLastTransactions()
        || await this.customersProvider.getTransactionsGroupedByStatus(0, null, false, TransactionViewType.Any);
      const latest = (transactions || []).find(transaction => !!transaction.CountryTo);
      if (!latest) {
        return undefined;
      }
      // The API sends UK for the United Kingdom. ISO 3166-1 uses GB.
      return latest.CountryTo === 'UK' ? 'GB' : latest.CountryTo;
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Ding top-up: transactions not loaded');
      return undefined;
    }
  }

  private async buildTokenBody(): Promise<{ countryTo?: string; currency?: string }> {
    const countryTo = await this.resolveCountryTo();
    const currency = APP_CONFIG.region ? APP_CONFIG.region.defaultCurrencyFrom : undefined;
    return {
      ...(countryTo ? { countryTo } : {}),
      ...(currency ? { currency } : {}),
    };
  }

  // Make a new token right before each open: the token is for one use only and has a short life.
  private async mintToken(): Promise<IDingTokenResult> {
    const timedOut = {};
    let timer: number = null;
    this.loadingProvider.show();
    try {
      const body = await this.buildTokenBody();
      // The flow shows its own error alert, so do not let the HTTP provider show a second alert.
      const request = this.httpProvider.post(`${APP_CONFIG.API_URL}api/PartnerSso/${DingTopUpProvider.PARTNER}/Token`, body, null, null, true);
      const timeout = new Promise(resolve => { timer = window.setTimeout(() => resolve(timedOut), DingTopUpProvider.tokenTimeoutMs()); });
      const response = await Promise.race([request, timeout]);
      if (response === timedOut) {
        LoggerProvider.error(this, LoggerEvent.ProviderException, 'Ding top-up: token request timed out', { timeoutMs: DingTopUpProvider.tokenTimeoutMs() });
        return { token: null };
      }
      // The HTTP provider returns no response when the relogin failed. Then the app goes to the start page.
      if (!response) {
        LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Ding top-up: token request returned no response');
        return { token: null, stop: true };
      }
      return { token: response.Value ? response.Value.Token || null : null, retVal: response.Result ? response.Result.RetVal : null };
    } catch (ex) {
      const status = ex ? ex.status : null;
      // After 401 or 403 the HTTP provider can send the customer to the start page, so do not show an error alert on top.
      const stop = DingTopUpProvider.AUTH_FAILURE_STATUSES.indexOf(status) >= 0;
      LoggerProvider.error(this, LoggerEvent.ProviderException, 'Ding top-up: token request failed', { status, stop });
      return { token: null, stop };
    } finally {
      window.clearTimeout(timer);
      this.loadingProvider.hide();
    }
  }

  private buildRequest(token: string): BridgePartnerCheckoutRequest {
    return {
      partner: DingTopUpProvider.PARTNER,
      url: DingTopUpProvider.buildCheckoutUrl(HttpProvider.currentLanguage),
      returnUrlPrefix: DingTopUpProvider.RETURN_URL,
      token,
    };
  }

  private async askRetry(errorCode: DingTopUpErrorCode): Promise<boolean> {
    const retry = await this.openChoiceSheet({
      image: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.MobileTopUp.FailedTitle'),
      subtitle: this.translate.instant('page.MobileTopUp.FailedDescriptionWithCode').replace('{code}', errorCode),
      primaryButtonText: this.translate.instant('page.MobileTopUp.FailedPrimaryAction'),
      secondaryButtonText: this.translate.instant('global.closeButton'),
    });
    if (retry === null) {
      LoggerProvider.error(this, LoggerEvent.ProviderException, 'Ding top-up: the failure alert could not show', { errorCode });
    }
    return !!retry;
  }

  // Resolves null when the sheet cannot open. A root change can destroy the sheet without a dismissal, so onDestroy also resolves.
  private async openChoiceSheet(params: { [key: string]: any }): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      let primarySelected = false;
      let sheet: { dismissModalAlert: () => void } = null;
      const opened = this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
        showPrimaryButton: true,
        showSecondaryButton: true,
        isModal: true,
        hideHeader: true,
        onInit: page => { sheet = page; },
        onBackCallback: () => sheet && sheet.dismissModalAlert(),
        onDestroy: () => resolve(primarySelected),
        primaryButtonAction: () => { primarySelected = true; },
        ...params,
      }, () => resolve(primarySelected));
      if (!opened) {
        resolve(null);
      }
    });
  }

  private async isFlowActive(flow: IDingFlow): Promise<boolean> {
    if (this.flow !== flow || flow.ownerId !== this.getOwnerId()) {
      return false;
    }
    return this.isAvailable();
  }

  private endFlow(reason: string) {
    if (this.flow) {
      LoggerProvider.info(this, 'Ding top-up: flow ended', { reason, runningMs: Date.now() - this.flow.startedAt });
      this.flow = null;
    }
  }

  // A lite user has no customer id. A new customer gets the reserved customer id as customer id, so the consent and the banner dismissal stay after the profile creation.
  private getOwnerId(): string {
    return this.httpProvider.getAuthCustomerId() || this.httpProvider.getAuthLiteReservedCustomerId() || null;
  }

  private getConsentKey(): string {
    return this.getOwnerKey(StorageType.DingConsentPrefix);
  }

  private getBannerDismissedKey(): string {
    return this.getOwnerKey(StorageType.MobileTopUpBannerDismissedPrefix);
  }

  private getOwnerKey(prefix: StorageType): string {
    const ownerId = this.getOwnerId();
    return ownerId ? `${prefix}${ownerId}` : null;
  }
}



// WEBPACK FOOTER //
// ./src/providers/ding-top-up/ding-top-up.ts